import type {
  CheckoutAttemptSource,
  CheckoutOrderGenerateInput,
  CheckoutOrderAttemptStatusValue,
  CheckoutOrderResult,
  CheckoutOrderSnapshot,
  CheckoutPaymentInstructions,
} from '@/lib/api'

const CHECKOUT_ATTEMPT_STORAGE_VERSION = 1
const CHECKOUT_ATTEMPT_STORAGE_KEY_PREFIX = `checkout-attempt-v${CHECKOUT_ATTEMPT_STORAGE_VERSION}`
const CHECKOUT_ATTEMPT_STORAGE_GLOBAL_KEY = `${CHECKOUT_ATTEMPT_STORAGE_KEY_PREFIX}:last`
const CHECKOUT_ATTEMPT_STORAGE_ANONYMOUS_KEY = `${CHECKOUT_ATTEMPT_STORAGE_KEY_PREFIX}:anonymous`
const CHECKOUT_ATTEMPT_STORAGE_TTL_MS = 1000 * 60 * 60 * 24
export const CHECKOUT_ATTEMPT_STORAGE_EVENT = 'checkout-attempt-storage-change'

const normalizeCheckoutStorageScope = (value?: string | null) => {
  const normalized = String(value ?? '').trim().replace(/[^a-zA-Z0-9:_-]+/g, '-')
  return normalized || null
}

export type PersistedCheckoutAttempt = {
  version: number
  createdAt: number
  updatedAt: number
  attemptId: string
  cartSignature?: string | null
  attemptSource: CheckoutAttemptSource
  status: CheckoutOrderAttemptStatusValue
  paymentMethod: CheckoutOrderResult['paymentMethod']
  checkoutSnapshot: CheckoutOrderSnapshot | null
  paymentInstructions: CheckoutPaymentInstructions | null
  retryRequest: CheckoutOrderGenerateInput | null
}

const sanitizeAttemptSource = (value: unknown): CheckoutAttemptSource => {
  const normalized = typeof value === 'string' ? value.trim().toLowerCase() : ''
  if (
    normalized === 'retry'
    || normalized === 'refresh_recovery'
    || normalized === 'fallback_recovery'
  ) {
    return normalized
  }
  return 'fresh_submit'
}

const sanitizeAttemptStatus = (value: unknown): CheckoutOrderAttemptStatusValue => {
  const normalized = typeof value === 'string' ? value.trim().toLowerCase() : ''
  if (normalized === 'success' || normalized === 'failed' || normalized === 'expired') {
    return normalized
  }
  return 'processing'
}

const sanitizeCartSignature = (value: unknown) => {
  const normalized = typeof value === 'string' ? value.trim() : ''
  return normalized || null
}

const sanitizePositiveInteger = (value: unknown) => {
  if (typeof value !== 'number' || !Number.isFinite(value) || value <= 0) return 0
  return Math.trunc(value)
}

const sanitizeCheckoutSnapshot = (value: unknown): CheckoutOrderSnapshot | null => {
  if (!value || typeof value !== 'object') return null

  const source = value as Partial<CheckoutOrderSnapshot>
  const subtotal = typeof source.subtotal === 'number' && Number.isFinite(source.subtotal)
    ? Math.max(0, source.subtotal)
    : null
  const desconto = typeof source.desconto === 'number' && Number.isFinite(source.desconto)
    ? Math.max(0, source.desconto)
    : null
  const frete = typeof source.frete === 'number' && Number.isFinite(source.frete)
    ? Math.max(0, source.frete)
    : null
  const impostos = typeof source.impostos === 'number' && Number.isFinite(source.impostos)
    ? Math.max(0, source.impostos)
    : null
  const total = typeof source.total === 'number' && Number.isFinite(source.total)
    ? Math.max(0, source.total)
    : null

  if (
    subtotal === null
    || desconto === null
    || frete === null
    || impostos === null
    || total === null
  ) {
    return null
  }

  const freteSelecionadoSource = source.freteSelecionado && typeof source.freteSelecionado === 'object'
    ? source.freteSelecionado
    : null

  return {
    subtotal,
    desconto,
    frete,
    impostos,
    total,
    freteSelecionado: freteSelecionadoSource
      ? {
          transportadoraId: sanitizePositiveInteger(freteSelecionadoSource.transportadoraId),
          cotacaoId: sanitizePositiveInteger(freteSelecionadoSource.cotacaoId),
          tipoFrete: freteSelecionadoSource.tipoFrete === 'fob' ? 'fob' : 'cif',
          fretePorConta: freteSelecionadoSource.fretePorConta === 1 ? 1 : 0,
          valorFrete: typeof freteSelecionadoSource.valorFrete === 'number' && Number.isFinite(freteSelecionadoSource.valorFrete)
            ? Math.max(0, freteSelecionadoSource.valorFrete)
            : 0,
          valorOriginal: typeof freteSelecionadoSource.valorOriginal === 'number' && Number.isFinite(freteSelecionadoSource.valorOriginal)
            ? Math.max(0, freteSelecionadoSource.valorOriginal)
            : 0,
        }
      : null,
  }
}

const sanitizePaymentMethod = (value: unknown): CheckoutOrderResult['paymentMethod'] => {
  if (!value || typeof value !== 'object') return null

  const source = value as NonNullable<CheckoutOrderResult['paymentMethod']>
  return {
    formaPagamentoId: sanitizePositiveInteger(source.formaPagamentoId),
    condicaoPagamentoId: sanitizePositiveInteger(source.condicaoPagamentoId),
    label: typeof source.label === 'string' ? source.label : '',
    description: typeof source.description === 'string' ? source.description : '',
    detail: typeof source.detail === 'string' ? source.detail : '',
  }
}

const sanitizeRetryRequest = (value: unknown): CheckoutOrderGenerateInput | null => {
  if (!value || typeof value !== 'object') return null

  const source = value as Partial<CheckoutOrderGenerateInput>
  const formaPagamentoId = sanitizePositiveInteger(source.formaPagamentoId)
  const condicaoPagamentoId = sanitizePositiveInteger(source.condicaoPagamentoId)
  const pedidoId = sanitizePositiveInteger(source.pedidoId)
  const isDirectOrderRepurchase = pedidoId > 0 && source.recompraDireta === true
  if (!isDirectOrderRepurchase && (formaPagamentoId <= 0 || condicaoPagamentoId <= 0)) return null

  const fretePorConta = source.fretePorConta === 1 ? 1 : 0
  const modoFrete = source.modoFrete === 'fob' || source.modoFrete === 'cif'
    ? source.modoFrete
    : 'auto'

  const retryRequest: CheckoutOrderGenerateInput = {
    fretePorConta,
    modoFrete,
  }

  if (formaPagamentoId > 0) retryRequest.formaPagamentoId = formaPagamentoId
  if (condicaoPagamentoId > 0) retryRequest.condicaoPagamentoId = condicaoPagamentoId

  const orcamentoId = sanitizePositiveInteger(source.orcamentoId)
  if (orcamentoId > 0) retryRequest.orcamentoId = orcamentoId

  if (pedidoId > 0) retryRequest.pedidoId = pedidoId
  if (isDirectOrderRepurchase) retryRequest.recompraDireta = true

  if (Array.isArray(source.pedidoItensOverride)) {
    const pedidoItensOverride = source.pedidoItensOverride
      .map((item) => ({
        itemId: sanitizePositiveInteger(item.itemId),
        quantity: typeof item.quantity === 'number' && Number.isFinite(item.quantity)
          ? Math.max(0, item.quantity)
          : 0,
      }))
      .filter((item) => item.itemId > 0)
    if (pedidoItensOverride.length > 0) retryRequest.pedidoItensOverride = pedidoItensOverride
  }

  const enderecoId = sanitizePositiveInteger(source.enderecoId)
  if (enderecoId > 0) retryRequest.enderecoId = enderecoId

  const transportadoraId = sanitizePositiveInteger(source.transportadoraId)
  if (transportadoraId > 0) retryRequest.transportadoraId = transportadoraId

  const cotacaoId = sanitizePositiveInteger(source.cotacaoId)
  if (cotacaoId > 0) retryRequest.cotacaoId = cotacaoId

  if (typeof source.transportadoraCotacaoFreteNumero === 'string') {
    const value = source.transportadoraCotacaoFreteNumero.trim()
    if (value) retryRequest.transportadoraCotacaoFreteNumero = value
  }

  if (typeof source.transportadoraCotacaoFreteValor === 'number' && Number.isFinite(source.transportadoraCotacaoFreteValor)) {
    retryRequest.transportadoraCotacaoFreteValor = Math.max(0, source.transportadoraCotacaoFreteValor)
  }

  if (typeof source.valorFrete === 'number' && Number.isFinite(source.valorFrete)) {
    retryRequest.valorFrete = Math.max(0, source.valorFrete)
  }

  if (typeof source.cupom === 'string') retryRequest.cupom = source.cupom
  retryRequest.usarCashback = source.usarCashback === true

  if (typeof source.lojaId === 'number' && Number.isFinite(source.lojaId)) {
    const lojaId = sanitizePositiveInteger(source.lojaId)
    if (lojaId > 0) retryRequest.lojaId = lojaId
  }

  if (typeof source.origem === 'string') {
    const origem = source.origem.trim()
    if (origem) retryRequest.origem = origem
  }

  if (typeof source.nomeContato === 'string') retryRequest.nomeContato = source.nomeContato
  if (typeof source.telefoneContato === 'string') retryRequest.telefoneContato = source.telefoneContato
  retryRequest.aceitaMarketing = source.aceitaMarketing === true

  if (typeof source.numeroOrdem === 'string') retryRequest.numeroOrdem = source.numeroOrdem

  if (typeof source.finalidade === 'number' && Number.isFinite(source.finalidade)) {
    retryRequest.finalidade = Math.max(0, Math.trunc(source.finalidade))
  }

  const habilitarProgramarFaturamento = source.habilitarProgramarFaturamento === true
    || source.habilitar_programar_faturamento === true
    || source.habilitar_programar_faturamento === 1
  if (habilitarProgramarFaturamento) {
    retryRequest.habilitarProgramarFaturamento = true
    const dataProgramacao = typeof source.dataProgramacao === 'string'
      ? source.dataProgramacao.trim()
      : typeof source.data_programacao === 'string'
        ? source.data_programacao.trim()
        : ''
    if (dataProgramacao) retryRequest.dataProgramacao = dataProgramacao
  }

  if (typeof source.obsFichaCarregamento === 'string') {
    retryRequest.obsFichaCarregamento = source.obsFichaCarregamento
  }

  if (Array.isArray(source.carrinhoIds)) {
    const carrinhoIds = source.carrinhoIds
      .map((item) => sanitizePositiveInteger(item))
      .filter((item) => item > 0)
    if (carrinhoIds.length > 0) retryRequest.carrinhoIds = carrinhoIds
  }

  retryRequest.checkoutSnapshot = sanitizeCheckoutSnapshot(source.checkoutSnapshot)

  return retryRequest
}

const emitAttemptStorageEvent = () => {
  if (typeof window === 'undefined') return
  window.dispatchEvent(new CustomEvent(CHECKOUT_ATTEMPT_STORAGE_EVENT))
}

const readPersistedCheckoutAttemptFromStorage = (
  storage: Storage,
  storageKey: string
): PersistedCheckoutAttempt | null => {
  try {
    const rawValue = storage.getItem(storageKey)
    if (!rawValue) return null

    const parsed = JSON.parse(rawValue) as Partial<PersistedCheckoutAttempt>
    const updatedAt = typeof parsed.updatedAt === 'number' ? parsed.updatedAt : 0
    if (!updatedAt || Date.now() - updatedAt > CHECKOUT_ATTEMPT_STORAGE_TTL_MS) {
      storage.removeItem(storageKey)
      return null
    }

    const attemptId = typeof parsed.attemptId === 'string' ? parsed.attemptId.trim() : ''
    if (!attemptId) {
      storage.removeItem(storageKey)
      return null
    }

    const createdAt = typeof parsed.createdAt === 'number' && parsed.createdAt > 0
      ? parsed.createdAt
      : updatedAt

    return {
      version: typeof parsed.version === 'number' ? parsed.version : CHECKOUT_ATTEMPT_STORAGE_VERSION,
      createdAt,
      updatedAt,
      attemptId,
      cartSignature: sanitizeCartSignature(parsed.cartSignature),
      attemptSource: sanitizeAttemptSource(parsed.attemptSource),
      status: sanitizeAttemptStatus(parsed.status),
      paymentMethod: sanitizePaymentMethod(parsed.paymentMethod),
      checkoutSnapshot: sanitizeCheckoutSnapshot(parsed.checkoutSnapshot),
      paymentInstructions: parsed.paymentInstructions ?? null,
      retryRequest: sanitizeRetryRequest(parsed.retryRequest),
    }
  } catch (error) {
    console.warn('Failed to read checkout attempt from storage:', error)
    return null
  }
}

export const buildCheckoutAttemptStorageKeys = (
  authenticatedEntidadeId?: number | null,
  storageScope?: string | null
) => {
  const scopedKey = authenticatedEntidadeId && authenticatedEntidadeId > 0
    ? `${CHECKOUT_ATTEMPT_STORAGE_KEY_PREFIX}:${Math.trunc(authenticatedEntidadeId)}`
    : CHECKOUT_ATTEMPT_STORAGE_ANONYMOUS_KEY
  const normalizedScope = normalizeCheckoutStorageScope(storageScope)
  const baseKeys = [
    scopedKey,
    CHECKOUT_ATTEMPT_STORAGE_GLOBAL_KEY,
    CHECKOUT_ATTEMPT_STORAGE_ANONYMOUS_KEY,
  ]

  return Array.from(new Set(
    baseKeys.map((key) => (normalizedScope ? `${key}:${normalizedScope}` : key))
  ))
}

export const readPersistedCheckoutAttempt = (storageKeys: string[]): PersistedCheckoutAttempt | null => {
  if (typeof window === 'undefined') return null

  for (const storageKey of storageKeys) {
    const fromSession = readPersistedCheckoutAttemptFromStorage(window.sessionStorage, storageKey)
    if (fromSession) return fromSession

    const fromLocal = readPersistedCheckoutAttemptFromStorage(window.localStorage, storageKey)
    if (fromLocal) return fromLocal
  }

  return null
}

export const writePersistedCheckoutAttempt = (storageKeys: string[], attempt: PersistedCheckoutAttempt) => {
  if (typeof window === 'undefined') return false

  const payload: PersistedCheckoutAttempt = {
    ...attempt,
    version: CHECKOUT_ATTEMPT_STORAGE_VERSION,
    createdAt: typeof attempt.createdAt === 'number' && attempt.createdAt > 0
      ? attempt.createdAt
      : Date.now(),
    updatedAt: Date.now(),
    cartSignature: sanitizeCartSignature(attempt.cartSignature),
    status: sanitizeAttemptStatus(attempt.status),
    attemptSource: sanitizeAttemptSource(attempt.attemptSource),
    paymentMethod: sanitizePaymentMethod(attempt.paymentMethod),
    checkoutSnapshot: sanitizeCheckoutSnapshot(attempt.checkoutSnapshot),
    paymentInstructions: attempt.paymentInstructions ?? null,
    retryRequest: sanitizeRetryRequest(attempt.retryRequest),
  }

  const serialized = JSON.stringify(payload)
  let hasPersisted = false

  storageKeys.forEach((storageKey) => {
    try {
      window.sessionStorage.setItem(storageKey, serialized)
      hasPersisted = true
    } catch (error) {
      console.warn('Failed to persist checkout attempt to sessionStorage:', error)
    }

    try {
      window.localStorage.setItem(storageKey, serialized)
      hasPersisted = true
    } catch (error) {
      console.warn('Failed to persist checkout attempt to localStorage:', error)
    }
  })

  emitAttemptStorageEvent()
  return hasPersisted
}

export const clearPersistedCheckoutAttempt = (storageKeys: string[]) => {
  if (typeof window === 'undefined') return

  storageKeys.forEach((storageKey) => {
    try {
      window.sessionStorage.removeItem(storageKey)
    } catch (error) {
      console.warn('Failed to clear checkout attempt from sessionStorage:', error)
    }

    try {
      window.localStorage.removeItem(storageKey)
    } catch (error) {
      console.warn('Failed to clear checkout attempt from localStorage:', error)
    }
  })

  emitAttemptStorageEvent()
}
